import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { Alert, Box, Grid, InputLabel, Stack, TextField, Typography } from '@mui/material'
import { registerInvitedTrainee } from 'api/auth'
import VerticalButtonBox from 'components/buttons/VerticalButtonBox'
import { useAuthContext } from 'components/context/AuthProvider'
import AgreeTermsCheckbox from 'components/form-elements/AgreeTermsCheckbox'
import FormInput from 'components/form-elements/FormInput'
import FormPhone from 'components/form-elements/FormPhone'
import FormSelect from 'components/form-elements/FormSelect'
import FormPasswordCheckList, { validatePassword } from 'components/FormPasswordCheckList'
import NotificationSys from 'components/NotificationSystem'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED, PASSWORD_REQUIREMENTS } from 'const'
import { educationOptions, preferredLanguageOptions, yearsOfExperience } from 'models/user.model'
import { useCallback, useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { router } from 'routes/router'
import { routes } from 'routes/routes'
import { EUserEducation, EUserExperience, TraineeInvitationInfo } from 'types'
import { boolean, object, string, TypeOf } from 'zod'
import { AuthFormTitle } from '../AuthFormTitle/AuthFormTitle'

interface Props {
  token: string
  info: TraineeInvitationInfo
}

const formSchema = object({
  firstName: string().min(1, 'First name is required'),
  lastName: string().min(1, 'Last name is required'),
  email: string().min(1, 'Email is required').email(EMAIL_IS_INVALID),
  phoneNumber: string().optional(),
  myNewPassword: string().refine(
    (password: string) => validatePassword(password).allCheck,
    PASSWORD_REQUIREMENTS,
  ),
  sameNewPassword: string().min(1, 'Password is required'),
  education: string().min(1, 'Education is required'),
  yearsOfExperience: string().min(1, FIELD_IS_REQUIRED),
  language: string().min(1, FIELD_IS_REQUIRED),
  languageOther: string(),
  readTermsAndConditions: boolean().refine(
    (val) => val,
    'Please confirm of reading terms and conditions.',
  ),
})
  .refine((data) => data.myNewPassword === data.sameNewPassword, {
    path: ['sameNewPassword'],
    message: 'Passwords do not match',
  })
  .refine((data) => !(data.language === 'other' && data.languageOther.trim() === ''), {
    path: ['languageOther'],
    message: FIELD_IS_REQUIRED,
  })

type FormType = TypeOf<typeof formSchema>

export function SignUpInvitedTraineeForm({ token, info }: Props) {
  const { authLoading, login } = useAuthContext()
  const navigate = useNavigate()

  const inviteEmail = info.email?.trim() || ''
  const isPhoneOnlyInvite = useMemo(
    () => !inviteEmail && !!info.phoneNumber?.trim(),
    [inviteEmail, info.phoneNumber],
  )

  const methods = useForm<FormType>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: info.firstName,
      lastName: info.lastName,
      email: inviteEmail,
      phoneNumber: info.phoneNumber || '',
      myNewPassword: '',
      sameNewPassword: '',
      education: '',
      yearsOfExperience: '',
      language: '',
      languageOther: '',
      readTermsAndConditions: false,
    },
  })

  const { handleSubmit, watch } = methods
  const languageValue = watch('language')
  const email = watch('email')
  const password = watch('myNewPassword')

  const onSuccessLogin = () => {
    setTimeout(() => {
      router.navigate(routes.journey)
    }, 0)
  }

  const onFailLogin = () => {
    navigate(routes.signIn)
    NotificationSys.showWarning(
      "Can't automatically sign-in after register. Please sign-in manually",
    )
  }

  const registerMutation = useMutation(registerInvitedTrainee, {
    onSuccess: () => {
      NotificationSys.showSuccess('Registration completed successfully')
      login(email, password, onSuccessLogin, onFailLogin)
    },
  })

  const onSubmitHandler: SubmitHandler<FormType> = useCallback(
    (values) => {
      registerMutation.mutate({
        token,
        firstname: values.firstName,
        lastname: values.lastName,
        password: values.myNewPassword,
        // Phone-only invites have no email on the invitation — send the email the user entered.
        email: isPhoneOnlyInvite ? values.email : undefined,
        phoneNumber: values.phoneNumber || undefined,
        education: values.education as EUserEducation,
        experience: values.yearsOfExperience as EUserExperience,
        language: values.language !== 'other' ? values.language : values.languageOther,
      })
    },
    [registerMutation, token, isPhoneOnlyInvite],
  )

  const onCancel = () => navigate(routes.signIn)

  return (
    <FormProvider {...methods}>
      <AuthFormTitle>Create Trainee Account</AuthFormTitle>

      <Typography variant="body2" color="text.secondary" mb={2}>
        You are invited to join {info.organizationName}. Complete your registration below.
      </Typography>

      {isPhoneOnlyInvite && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          You were invited via text message. Please enter your email address — it will be used to
          sign in to your account.
        </Alert>
      )}

      <Grid
        container
        spacing={1.5}
        component="form"
        onSubmit={handleSubmit(onSubmitHandler)}
        noValidate
        autoComplete="off"
      >
        <Grid item xs={12} sm={6}>
          <FormInput label="First Name" name="firstName" required fullWidth disabled />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormInput label="Last Name" name="lastName" required fullWidth disabled />
        </Grid>

        <Grid item xs={12}>
          <FormInput
            label="My Email"
            name="email"
            required
            fullWidth
            autoComplete="off"
            disabled={!!inviteEmail}
          />
        </Grid>

        <Grid item xs={12}>
          <FormPhone
            label="Mobile Phone"
            name="phoneNumber"
            disabled={!!info.phoneNumber?.trim()}
          />
        </Grid>

        <Grid item xs={12}>
          <Stack spacing={1}>
            <InputLabel>Unit</InputLabel>
            <TextField
              value={info.accessCode?.specificUnitName ?? ''}
              fullWidth
              size="small"
              disabled
            />
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Stack spacing={1}>
            <InputLabel>Institutional Access Code</InputLabel>
            <TextField value={info.accessCode?.code ?? ''} fullWidth size="small" disabled />
            <Box component="span" sx={{ typography: 'caption', color: 'text.secondary' }}>
              This access code is assigned to your invitation and cannot be changed.
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} sm={12}>
          <FormInput
            label="My new password"
            required
            fullWidth
            name="myNewPassword"
            type="password"
            autoComplete="off"
          />
          <FormPasswordCheckList name="myNewPassword" />
        </Grid>
        <Grid item xs={12} sm={12}>
          <FormInput
            label="Same new password"
            required
            fullWidth
            name="sameNewPassword"
            type="password"
            autoComplete="off"
          />
        </Grid>

        <Grid item xs={12}>
          <FormSelect
            label="How much education did you complete?"
            name="education"
            required
            options={educationOptions}
          />
        </Grid>

        <Grid item xs={12}>
          <FormSelect
            label="Years of experience as a professional caregiver"
            name="yearsOfExperience"
            required
            options={yearsOfExperience}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <FormSelect
            label="What is your preferred language?"
            name="language"
            required
            options={preferredLanguageOptions}
          />
        </Grid>
        {languageValue === 'other' && (
          <Grid item sm={6} xs={12}>
            <FormInput label="Other" required fullWidth name="languageOther" sx={{ mb: 1 }} />
          </Grid>
        )}

        <Grid item xs={12}>
          <AgreeTermsCheckbox name="readTermsAndConditions" />
        </Grid>

        <Grid item xs={12}>
          <VerticalButtonBox
            mt={1}
            onCancel={onCancel}
            buttonSaveType="submit"
            saveText="Create my Account"
            loading={registerMutation.isLoading || authLoading}
          />
        </Grid>
      </Grid>
    </FormProvider>
  )
}
